import { css } from '@emotion/react';
import { math } from 'polished';
import Mixin from '../../../util/mixin';


// Breakpoint viewport sizes and media queries.
//
// Breakpoints are defined as a map of (name: minimum width), order from small to large:
//
//    (xs: 0, sm: 576px, md: 768px, lg: 992px, xl: 1200px)
//
// The map defined in the `$grid-breakpoints` global variable is used as the `$breakpoints` argument by default.

// Name of the next breakpoint, or null for the last breakpoint.
//
//    >> breakpoint-next(sm)
//    md
//    >> breakpoint-next(sm, (xs: 0, sm: 576px, md: 768px, lg: 992px, xl: 1200px))
//    md
//    >> breakpoint-next(sm, $breakpoint-names: (xs sm md lg xl))
//    md
export function breakpointNext($, name, breakpoints=undefined, breakpointNames=undefined) {
  if (breakpoints === undefined) breakpoints = $.gridBreakpoints;
  if (breakpointNames === undefined) breakpointNames = Object.keys(breakpoints);

  const n = breakpointNames.indexOf(name);
  const n2 = n + 1;
  return n >= 0 && n2 < breakpointNames.length ? breakpointNames[n + 1] : null;
}


// Minimum breakpoint width. Null for the smallest (first) breakpoint.
//
//    >> breakpoint-min(sm, (xs: 0, sm: 576px, md: 768px, lg: 992px, xl: 1200px))
//    576px
export function breakpointMin($, name, breakpoints=undefined) {
  if (breakpoints === undefined) breakpoints = $.gridBreakpoints;

  const min = breakpoints[name];

  return min != 0 ? min : null;
}


// Maximum breakpoint width. Null for the largest (last) breakpoint.
// The maximum value is calculated as the minimum of the next one less 0.02px
// to work around the limitations of `min-` and `max-` prefixes and viewports with fractional widths.
// See https://www.w3.org/TR/mediaqueries-4/#mq-min-max
// Uses 0.02px rather than 0.01px to work around a current rounding bug in Safari.
// See https://bugs.webkit.org/show_bug.cgi?id=178261
//
//    >> breakpoint-max(sm, (xs: 0, sm: 576px, md: 768px, lg: 992px, xl: 1200px))
//    767.98px
export function breakpointMax($, name, breakpoints = undefined) {
  if (breakpoints === undefined) breakpoints = $.gridBreakpoints;

  const next = breakpointNext($, name, breakpoints);
  return next ? math(`${breakpointMin($, next, breakpoints)} - 0.02`) : null;
}


// Returns a blank string if smallest breakpoint, otherwise returns the name with a dash in front.
// Useful for making responsive utilities.
//
//    >> breakpoint-infix(xs, (xs: 0, sm: 576px, md: 768px, lg: 992px, xl: 1200px))
//    ""  (Returns a blank string)
//    >> breakpoint-infix(sm, (xs: 0, sm: 576px, md: 768px, lg: 992px, xl: 1200px))
//    "-sm"
export function breakpointInfix($, name, breakpoints = undefined) {
  if (breakpoints === undefined) breakpoints = $.gridBreakpoints;

  return breakpointMin($, name, breakpoints) == null ? "" : `-${name}`;
}


// Media of at least the minimum breakpoint width. No query for the smallest breakpoint.
// Makes the @content apply to the given breakpoint and wider.
export class MediaBreakpointUp extends Mixin {
  style({$, content}, [name, breakpoints = undefined]) {
    breakpoints = breakpoints ?? $.gridBreakpoints

    const min = breakpointMin($, name, breakpoints);

    if (min) {
      return css`@media (min-width: ${min}) {
        ${content};
      }`
    } else {
      return content;
    }
  }
};

// Media of at most the maximum breakpoint width. No query for the largest breakpoint.
// Makes the @content apply to the given breakpoint and narrower.
export class MediaBreakpointDown extends Mixin {
  style({$, content}, [name, breakpoints = undefined]) {
    breakpoints = breakpoints ?? $.gridBreakpoints

    const max = breakpointMax($, name, breakpoints);

    if (max) {
      return css`@media (max-width: ${max}) {
        ${content};
      }`
    } else {
      return content;
    }
  }
};

// Media that spans multiple breakpoint widths.
// Makes the @content apply between the min and max breakpoints
export class MediaBreakpointBetween extends Mixin {
  style({$, content}, [lower, upper, breakpoints = undefined]) {
    breakpoints = breakpoints ?? $.gridBreakpoints

    const min = breakpointMin($, lower, breakpoints);
    const max = breakpointMax($, upper, breakpoints);

    if (min != null && max != null) {
      return css`@media (min-width: ${min}) and (max-width: ${max}) {
        ${content};
      }`
    } else if (max == null) {
      return mediaBreakpointUp({ $, content }, [lower, breakpoints]);
    } else if (min == null) {
      return mediaBreakpointDown({ $, content }, [upper, breakpoints]);
    }
  }
}

// Media between the breakpoint's minimum and maximum widths.
// No minimum for the smallest breakpoint, and no maximum for the largest one.
// Makes the @content apply only to the given breakpoint, not viewports any wider or narrower.
export class MediaBreakpointOnly extends Mixin {
  style({$, content}, [name, breakpoints = undefined]) {
    breakpoints = breakpoints ?? $.gridBreakpoints

    const min = breakpointMin($, lower, breakpoints);
    const max = breakpointMax($, upper, breakpoints);

    if (min != null && max != null) {
      return css`@media (min-width: ${min}) and (max-width: ${max}) {
        ${content};
      }`
    } else if (max == null) {
      return mediaBreakpointUp({$, content}, [name, breakpoints]);
    } else if (min == null) {
      return mediaBreakpointDown({$, content}, [name, breakpoints]);
    }
  }
}
